import { Radio, Button, Divider, Tooltip, Popconfirm } from 'antd';
import type { CheckboxOptionType, RadioGroupProps } from 'antd';
import {
  DeleteOutlined,
  UpCircleOutlined,
  DownCircleOutlined,
  VerticalAlignTopOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import type { DescendantExecuteContext } from 'views/Aide/Scenario/PolicyForm';
import type { MsgType } from 'components/TemplateEditor/mustacheUtils';
import { ImageElement, CustomText } from 'components/TemplateEditor/slateTypes';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import styled from '@emotion/styled';

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: var(--ant-primary-1);
  > section {
    flex: 1 0 0;
  }
`;

const Operation = styled.div`
  flex: 0 0 auto;
  svg {
    color: #84868c;
  }
  svg:hover {
    color: #222;
  }
  .ant-btn[disabled] {
    svg {
      opacity: 0.6;
    }
  }
`;

const MsgTypesRadioGroup = ({
  platform,
  idxType,
  delMsg,
  moveUp,
  moveDown,
  moveTop,
  msgTypeOpts,
  checkedMsgTypesLen,
  valuePartial,
  curChkValue,
  switchMsgType,
  contentItemList,
  isKnowledge = false,
  disabled,
  ...props
}: RadioGroupProps & {
  checkedMsgTypesLen: number;
  platform?: General.PlatformUpperCase;
  msgTypeOpts: CheckboxOptionType[];
  idxType: number;
  moveUp: () => void;
  moveDown: () => void;
  moveTop: () => void;
  delMsg: () => void;
  valuePartial: DescendantExecuteContext;
  curChkValue: DescendantExecuteContext;
  switchMsgType: (newType: MsgType) => void;
  contentItemList?: Aide.Policy['contentItemList'];
  isKnowledge?: boolean;
}) => {
  const { t } = useTranslate();

  const { value: chkValue } = props;

  const typeName = {
    text: t('phrase.文本'),
    sticker: t('messageType.STICKER'),
    image: t('phrase.图片'),
    video: t('phrase.视频'),
    follow: (
      <>
        {t('phrase.邀请关注')}
        {platform === 'SHOPEE' ? (
          <Tooltip title={t('phrase.邀请关注提示')}>
            <InfoCircleOutlined
              className={css`
                margin-left: 4px;
              `}
            />
          </Tooltip>
        ) : null}
      </>
    ),
    qrcode: '自动发货二维码',
    item: t('messageType.商品卡片'),
    logistics: t('物流卡片'),
  };
  msgTypeOpts.forEach((v) => {
    // eslint-disable-next-line no-param-reassign
    v.label = typeName[v.value as keyof typeof typeName];
  });

  const canDirectDel = useMemo(() => {
    let len = 0;

    valuePartial.content.forEach((v) => {
      if (v.type === 'paragraph') {
        len += v.children.length;
      } else if (v.type === 'image') {
        len += v.images.length;
      } else if (v.type === 'item') {
        len += contentItemList?.length || 0;
      }
    });

    valuePartial.translatedContent.forEach((v) => {
      if (v.type === 'paragraph') {
        len += v.children.length;
      } else if (v.type === 'image') {
        len += v.images.length;
      } else if (v.type === 'item') {
        len += contentItemList?.length || 0;
      }
    });

    return len === 0;
  }, [valuePartial, contentItemList]);

  const OpBtns = (
    <Operation>
      <Popconfirm
        disabled={canDirectDel || checkedMsgTypesLen === 1}
        title={t('upload.你确定要删除吗')}
        onConfirm={delMsg}
      >
        <Tooltip title={t('marketing.删除')}>
          <Button
            onClick={canDirectDel ? delMsg : undefined}
            type="text"
            icon={<DeleteOutlined />}
            disabled={checkedMsgTypesLen === 1 || disabled}
          />
        </Tooltip>
      </Popconfirm>
      <Divider type="vertical" />
      <Tooltip title={t('marketing.置顶')}>
        <Button
          type="text"
          icon={<VerticalAlignTopOutlined />}
          onClick={moveTop}
          disabled={idxType === 0 || disabled}
        />
      </Tooltip>
      <Tooltip title={t('marketing.上移')}>
        <Button
          type="text"
          icon={<UpCircleOutlined />}
          onClick={moveUp}
          disabled={idxType === 0 || disabled}
        />
      </Tooltip>
      <Tooltip title={t('marketing.下移')}>
        <Button
          type="text"
          icon={<DownCircleOutlined />}
          onClick={moveDown}
          disabled={idxType === checkedMsgTypesLen - 1 || disabled}
        />
      </Tooltip>
    </Operation>
  );

  const curChkHasValue =
    chkValue === 'text'
      ? curChkValue.content.some(
          (v) =>
            v.type === CustomSlateNodeType.PARAGRAPH && (v.children[0] as CustomText)?.text !== '',
        ) ||
        curChkValue.translatedContent.some(
          (v) =>
            v.type === CustomSlateNodeType.PARAGRAPH && (v.children[0] as CustomText)?.text !== '',
        )
      : chkValue === 'item'
      ? Boolean(contentItemList?.length)
      : curChkValue.content.some((v) => (v as ImageElement)?.images?.length > 0) ||
        curChkValue.translatedContent.some((v) => (v as ImageElement)?.images?.length > 0);

  return (
    <Container>
      <section>
        <span>
          {t('aide.消息')} {idxType + 1}
        </span>
        <Divider type="vertical" style={{ borderLeft: '1px solid rgba(0,0,0,.26)' }} />
        <span style={{ marginRight: '8px' }}>{t('aide.选择类型')} :</span>
        <Radio.Group
          {...props}
          disabled={disabled}
          onChange={(e) => {
            if (!curChkHasValue) {
              switchMsgType(e.target.value);
            }
          }}
        >
          {msgTypeOpts.map((v) =>
            v.value === 'item' && isKnowledge && !v.disabled ? (
              <Tooltip title={t('policy_content_item_disabled_tips')}>
                <Radio disabled value={v.value}>
                  {v.label}
                </Radio>
              </Tooltip>
            ) : (
              <Popconfirm
                placement="topLeft"
                title={t('phrase.是否清空回复内容切换至该类型')}
                disabled={!curChkHasValue || v.value === chkValue}
                onConfirm={() => {
                  switchMsgType(v.value as MsgType);
                }}
              >
                <Radio value={v.value} disabled={v.disabled}>
                  {v.label}
                </Radio>
              </Popconfirm>
            ),
          )}
        </Radio.Group>
      </section>
      {OpBtns}
    </Container>
  );
};

export default MsgTypesRadioGroup;
